import React from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import {
  TranscriptionImproveStats,
  TranscriptionImproveMode,
} from '../utils/transcription-improve';

const cn = createCn('file', classes);

export type { TranscriptionImproveStats };

type Props = {
  stats: TranscriptionImproveStats;
  mode: TranscriptionImproveMode;
  busy?: boolean;
  fullImproveBusy?: boolean;
  retranscribeBusy?: boolean;
  reprocessDeepgramBusy?: boolean;
  protocolBusy?: boolean;
  onFullImprove: () => void;
  onRetranscribeAndProtocol: () => void;
  onReprocessDeepgram?: () => void;
  onRegenerateProtocol?: () => void;
};

const TranscriptionImprovePanel = ({
  stats,
  mode,
  busy = false,
  fullImproveBusy = false,
  retranscribeBusy = false,
  reprocessDeepgramBusy = false,
  protocolBusy = false,
  onFullImprove,
  onRetranscribeAndProtocol,
  onReprocessDeepgram,
  onRegenerateProtocol,
}: Props) => {
  if (!stats.segmentCount) return null;

  const hasUnidentifiedSpeakers = stats.unidentifiedSpeakerCount > 0;
  const onlyProtocol = mode.showProtocolOnly && !mode.showPyannote && !mode.showRetranscribe && !mode.showReprocessDeepgram;

  return (
    <div className={cn('transcription-improve')}>
      <div className={cn('transcription-improve-header')}>
        <h3 className={cn('transcription-improve-title')}>Улучшение транскрипции и протокола</h3>
        <p className={cn('transcription-improve-hint')}>
          {stats.speakerCount} спикер(ов), {stats.segmentCount} реплик.
          {hasUnidentifiedSpeakers && (
            <>
              {' '}
              Без имени: {stats.unidentifiedSpeakerCount} спикер(ов).
              {mode.kind === 'deepgram' || mode.kind === 'video'
                ? ' Привяжите имена вручную или добавьте голосовые образцы в «Спикеры».'
                : ' Добавьте образцы в «Спикеры» или запустите полное улучшение (Pyannote).'}
            </>
          )}
          {' '}
          {mode.description}
        </p>
      </div>

      {!onlyProtocol && (
        <div className={cn('transcription-improve-steps')}>
          {mode.showPyannote && (
            <div className={cn('transcription-improve-step')}>
              <span className={cn('transcription-improve-step-num')}>1</span>
              <div>
                <strong>Больше спикеров (Pyannote)</strong> — заново разделит голоса (обычно 8–11 на совещании),
                сопоставит с базой спикеров и пересоздаст протокол (10–20 мин).
              </div>
            </div>
          )}
          {mode.showRetranscribe && (
            <div className={cn('transcription-improve-step')}>
              <span className={cn('transcription-improve-step-num')}>
                {mode.showPyannote ? '2' : '1'}
              </span>
              <div>
                <strong>Ретранскрибация</strong> — перепишет текст по текущим сегментам и обновит
                протокол (3–8 мин). <em>Количество спикеров не меняется.</em>
              </div>
            </div>
          )}
          {mode.showReprocessDeepgram && (
            <div className={cn('transcription-improve-step')}>
              <span className={cn('transcription-improve-step-num')}>
                {(mode.showPyannote ? 1 : 0) + (mode.showRetranscribe ? 1 : 0) + 1}
              </span>
              <div>
                <strong>Повторная диаризация</strong> — та же диаризация с min/max из .env (на этом аудио часто снова 2–3 спикера).
              </div>
            </div>
          )}
        </div>
      )}

      <div className={cn('transcription-improve-actions')}>
        {mode.showPyannote && (
          <button
            type="button"
            className={cn('download-btn')}
            disabled={busy}
            onClick={onFullImprove}
          >
            {fullImproveBusy ? 'Pyannote…' : 'Больше спикеров (Pyannote)'}
          </button>
        )}
        {mode.showRetranscribe && (
          <button
            type="button"
            className={cn('download-btn', { secondary: true })}
            disabled={busy}
            onClick={onRetranscribeAndProtocol}
          >
            {retranscribeBusy ? 'Ретранскрибация…' : 'Ретранскрибация + протокол'}
          </button>
        )}
        {mode.showReprocessDeepgram && onReprocessDeepgram && (
          <button
            type="button"
            className={cn('download-btn')}
            disabled={busy}
            onClick={onReprocessDeepgram}
          >
            {reprocessDeepgramBusy ? 'Обработка…' : 'Повторить диаризацию'}
          </button>
        )}
        {onRegenerateProtocol && mode.showProtocolOnly && (
          <button
            type="button"
            className={cn('download-btn', { secondary: !onlyProtocol })}
            disabled={busy}
            onClick={onRegenerateProtocol}
          >
            {protocolBusy ? 'Протокол…' : onlyProtocol ? 'Перегенерировать протокол' : 'Только протокол'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TranscriptionImprovePanel;
